import { useEffect, useMemo, useState, useContext } from 'react';
import { Button, Col, Collapse, Form, Input, Row, Switch, Typography } from 'antd';
import { useSelector } from 'react-redux';
import { useNavigate, useParams } from 'react-router-dom';
import { get, post, put } from '../../api/base';
import { RootState } from '../../store/store';
import { NotificationContext } from '../../providers/NotificationProvider';
import { useTranslation } from 'react-i18next';

const LABEL_KEYS = ['nodes', 'deviations', 'causes', 'consequences', 'safeguards', 'recommendations'] as const;
const LANGUAGE_CODES = ['es', 'en'] as const;

type MethodologyAdminLabels = Record<string, Record<string, string>>;

type MethodologyAdminType = {
  id: number;
  name: string;
  labels: MethodologyAdminLabels;
  show_risk_analysis: boolean;
  show_safeguards: boolean;
  show_recommendations: boolean;
};

const MethodologyForm: React.FC = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { id } = useParams();
  const isNew = !id;

  const { access, rol, user } = useSelector((state: RootState) => state.user);
  const { triggerNotification } = useContext(NotificationContext);
  const [loading, setLoading] = useState(false);
  const [form] = Form.useForm();
  const showSafeguards = Form.useWatch('show_safeguards', form);
  const showRecommendations = Form.useWatch('show_recommendations', form);

  const isSettingsAdmin = rol === 'Administrador' || user?.is_staff;

  const headers = useMemo(() => {
    return access ? { Authorization: `Bearer ${access}` } : undefined;
  }, [access]);

  const buildEmptyLabels = (): MethodologyAdminLabels => {
    const initialLabels: MethodologyAdminLabels = {};
    LABEL_KEYS.forEach((key) => {
      initialLabels[key] = {};
      LANGUAGE_CODES.forEach((lang) => {
        initialLabels[key][lang] = '';
      });
    });
    return initialLabels;
  };

  const buildInitialValues = (overrides?: Partial<MethodologyAdminType>) => ({
    name: overrides?.name ?? '',
    labels: {
      ...buildEmptyLabels(),
      ...(overrides?.labels ?? {}),
    },
    show_risk_analysis: overrides?.show_risk_analysis ?? true,
    show_safeguards: overrides?.show_safeguards ?? true,
    show_recommendations: overrides?.show_recommendations ?? true,
  });

  useEffect(() => {
    if (!isSettingsAdmin) {
      navigate('/proyectos');
      return;
    }
    if (isNew) {
      form.setFieldsValue(buildInitialValues());
      return;
    }
    if (Number.isNaN(Number(id))) {
      navigate('/configuraciones');
      return;
    }
    setLoading(true);
    get(`methodologies/${id}/`, { headers })
      .then((response: any) => {
        const data: MethodologyAdminType = response?.data;
        form.setFieldsValue(buildInitialValues(data));
      })
      .catch(() => {
        triggerNotification('error', t('Error'), t('Error al cargar las metodologías'), 'bottomRight');
      })
      .finally(() => setLoading(false));
    // eslint-disable-next-line
  }, [id, isNew, isSettingsAdmin]);

  const handleSave = async () => {
    try {
      const values = await form.validateFields();
      const payload = {
        name: values.name,
        labels: values.labels,
        show_risk_analysis: values.show_risk_analysis,
        show_safeguards: values.show_safeguards,
        show_recommendations: values.show_recommendations,
      };
      if (isNew) {
        await post('methodologies/', payload, { headers });
        triggerNotification('success', t('Éxito'), t('Metodología creada'), 'bottomRight');
      } else {
        await put(`methodologies/${id}/`, payload, { headers });
        triggerNotification('success', t('Éxito'), t('Metodología actualizada'), 'bottomRight');
      }
      navigate('/configuraciones');
    } catch (error) {
      // errores de validación ya mostrados por el form
    }
  };

  return (
    <Row className='content' style={{ margin: 24 }}>
      <Col span={24}>
        <div className='title-full'>
          <Typography.Title level={2} style={{ margin: 0 }}>
            {isNew ? t('Nueva Metodología') : t('Editar Metodología')}
          </Typography.Title>
        </div>
        <div className='ant-divider' />

        <Form form={form} layout='vertical'>
          <Collapse
            defaultActiveKey={['general', 'visibility', 'labels']}
            items={[
              {
                key: 'general',
                label: t('General'),
                children: (
                  <Form.Item
                    label={t('Nombre de la metodología')}
                    name='name'
                    rules={[{ required: true, message: t('Por favor ingresa el nombre') }]}
                  >
                    <Input disabled={loading} />
                  </Form.Item>
                ),
              },
              {
                key: 'visibility',
                label: t('Columnas'),
                children: (
                  <>
                    <Form.Item
                      label={t('Mostrar análisis de riesgo')}
                      name='show_risk_analysis'
                      valuePropName='checked'
                    >
                      <Switch disabled={loading} />
                    </Form.Item>
                    <Form.Item
                      label={t('Mostrar salvaguardas')}
                      name='show_safeguards'
                      valuePropName='checked'
                    >
                      <Switch disabled={loading} />
                    </Form.Item>
                    <Form.Item
                      label={t('Mostrar recomendaciones')}
                      name='show_recommendations'
                      valuePropName='checked'
                    >
                      <Switch disabled={loading} />
                    </Form.Item>
                  </>
                ),
              },
              {
                key: 'labels',
                label: t('Etiquetas'),
                children: (
                  <>
                    {LABEL_KEYS.filter((key) => {
                      if (key === 'safeguards') return showSafeguards !== false;
                      if (key === 'recommendations') return showRecommendations !== false;
                      return true;
                    }).map((key) => (
                      <Row key={key} gutter={16} style={{ marginBottom: 8 }}>
                        <Col span={4} style={{ display: 'flex', alignItems: 'center' }}>
                          <strong>
                            {t(
                              key === 'nodes'
                                ? 'Nodos'
                                : key === 'deviations'
                                ? 'Desviaciones'
                                : key === 'causes'
                                ? 'Causas'
                                : key === 'consequences'
                                ? 'Consecuencias'
                                : key === 'safeguards'
                                ? 'Salvaguardas'
                                : 'Recomendaciones'
                            )}
                          </strong>
                        </Col>
                        {LANGUAGE_CODES.map((lang) => (
                          <Col span={10} key={lang}>
                            <Form.Item
                              label={lang.toUpperCase()}
                              name={['labels', key, lang]}
                              rules={[{ required: true, message: t('Por favor ingresa el nombre') }]}
                            >
                              <Input disabled={loading} />
                            </Form.Item>
                          </Col>
                        ))}
                      </Row>
                    ))}
                  </>
                ),
              },
            ]}
          />

          <div style={{ display: 'flex', gap: 12, marginTop: 16 }}>
            <Button type='primary' onClick={handleSave} loading={loading}>
              {t('Guardar')}
            </Button>
            <Button onClick={() => navigate('/configuraciones')}>{t('Cancelar')}</Button>
          </div>
        </Form>
      </Col>
    </Row>
  );
};

export default MethodologyForm;
